import { useEffect, useMemo, useState } from 'react';
import type { ApiClient, SanitaerBaumknoten, SanitaerRaum } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState, Vorbehalte, WertEinheit, formatiereZahl, type Spalte, type Vorbehalt,
} from '../muster';
import './sanitaer/sanitaer.css';

/**
 * Der Sanitärbestand als Baum: Geschoss → Raum → Gegenstandsart → Gegenstand.
 * Rohre und Formteile stehen als eigene, nicht weiter aufklappbare Zeile je
 * Bauteilart am Raum - der Baum selbst geht bis zum einzelnen GEGENSTAND
 * (WC, Waschtisch, Spüle …), weil dort der Anschlusswert und die
 * Netzteilzuordnung stehen (S1, `auftraege/S1-sanitaer-navigation-und-baum.md`).
 *
 * --- Warum ein Baum und keine Liste -------------------------------------------
 *
 * 19.921 Rohre und 4.900 Gegenstände sind als flache Liste nicht benutzbar. Die
 * Frage, die jemand an den Bestand stellt, lautet außerdem selten „zeig mir alle
 * Rohre", sondern „was steht in diesem Raum".
 *
 * Dieselbe Gliederung wie bei der Heizung — wer dort sucht, sucht hier gleich.
 *
 * --- Warum Rohre und Gegenstände zusammen -------------------------------------
 *
 * Sie stehen in zwei Tabellen, aber im selben Raum. Wer die Verortung der
 * Komponenten sucht, will das WC und die Leitung dazu sehen — und nicht zweimal
 * blättern.
 *
 * --- Warum die Blätter auf Abruf kommen ---------------------------------------
 *
 * Ein Baum, der alle 24.000 Elemente trägt, sprengt den nginx-Puffer um ein
 * Vielfaches. Er trägt deshalb nur Struktur und Zahlen; die Bauteile eines Raums
 * werden geholt, wenn jemand ihn öffnet.
 *
 * --- Was hier NICHT steht: der Ast "ohne Netzteil" ----------------------------
 *
 * Befund B1 (`docs/PLAN-2026-09-20-sanitaermodul.md`): 59 von 2.202
 * Gegenständen mit Anschlusswert lassen sich keinem Abwassernetzteil
 * zuordnen - ihr DU geht in keine Summe ein. Ein eigener, sichtbarer Ast
 * dafür ist ausdrücklicher Auftrag (S1). Er fehlt hier bewusst: Weder
 * `sanitaer/raum` noch `sanitaer/netzteile` liefern eine Zuordnung
 * Gegenstand → Netzteil (`sanitaer_anschlusswerte` trägt keine
 * `NetzteilId`, und `/sanitaer/netzteile` listet nur Berechnungszeilen
 * bereits zugeordneter Netzteile). Ohne diese Kette liesse sich der Ast nur
 * mit geratenen Zahlen füllen - das ist die Lücke, die als Hinweis unten
 * steht, und keine Codelücke, die man "noch schnell" ergänzt (siehe S1-
 * Bericht und S2/S3 im Plan).
 *
 * --- TX-STANDARD S1 (TS9b1b, TD3) ---------------------------------------------
 *
 * `PageHeader` in jedem Zustand, der Hinweis B1 als Vorbehalt unter dem Kopf,
 * Kennzahlen als `KpiReihe`, die Knoten als Knöpfe (`aria-expanded`) statt
 * anklickbarer `div` (S1.10 Nr. 2), Zahlen über `formatiereZahl`, Zustände über
 * die Muster mit „Erneut versuchen“ - auch beim Holen der Bauteile eines Raums.
 */

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

/**
 * Befund B1 als Vorbehalt (S1.2 Nr. 2): Der Ast „ohne Netzteil“ fehlt absichtlich - die Zuordnung Gegenstand →
 * Netzteil liefert keine Route; ihn mit geratenen Zahlen zu füllen hieße, die Lücke zu verstecken.
 */
export const VORBEHALT_B1: Vorbehalt = {
  art: 'sonstiges',
  text: 'Ast „ohne Netzteil" fehlt hier absichtlich. Befund B1 nennt 59 von 2.202 Gegenständen mit Anschlusswert ohne '
    + 'Abwassernetzteil - ihr DU geht in keine Summe ein. Weder /sanitaer/raum noch /sanitaer/netzteile liefern die '
    + 'Zuordnung Gegenstand → Netzteil; ohne diese Kette ließe sich der Ast nur mit geratenen Zahlen füllen. Das ist ein '
    + 'offener Punkt für die Modellbezüge (Plan, Abschnitt S3), kein Fehler der Seite.',
};

const KREISNAMEN: Record<string, string> = {
  trinkwasser_kalt: 'Trinkwasser kalt',
  trinkwasser_warm: 'Trinkwasser warm',
  zirkulation: 'Zirkulation',
  schmutzwasser: 'Schmutzwasser',
  schmutzwasser_grund: 'Grundleitung',
  entlueftung: 'Entlüftung',
  kondensat: 'Kondensat',
  regenwasser: 'Regenwasser',
  notentwaesserung: 'Notentwässerung',
  druckleitung: 'Druckleitung',
  fettabwasser: 'Fettabwasser',
};

const ARTNAMEN: Record<string, string> = {
  rohr: 'Rohre',
  flexrohr: 'Flexrohre',
  formteil: 'Formteile',
  zubehoer: 'Zubehör',
  ablauf: 'Abläufe',
  geraet: 'Geräte',
  wc: 'WC',
  urinal: 'Urinale',
  urinaltrennwand: 'Urinaltrennwände',
  waschtisch: 'Waschtische',
  dusche: 'Duschen',
  spuele: 'Spülen',
  bodenablauf: 'Bodenabläufe',
  kuechenanschluss: 'Küchenanschlüsse',
  dachrinne: 'Dachrinnen',
  ohne_wasser: 'ohne Wasseranschluss',
  sonstiges: 'ohne zugeordnete Art',
};

export function SanitaerBaum({ api, projektId }: Eigenschaften) {
  const [baum, setBaum] = useState<SanitaerBaumknoten[] | null>(null);
  const [kopf, setKopf] = useState<{
    geschosse: number; raeume: number; elemente: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const [nurGeraete, setNurGeraete] = useState(false);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());

  useEffect(() => {
    setBaum(null);
    setFehler(null);

    api.sanitaerBaum(projektId, nurGeraete)
      .then((b) => {
        setBaum(b.baum);
        setKopf({ geschosse: b.geschosse, raeume: b.raeume,
                  elemente: b.elemente });
      })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, nurGeraete, versuch]);

  // Die Suche filtert im Browser. Der Baum trägt nur Struktur und Zahlen —
  // ein Rundgang zum Server je Tastendruck wäre nicht schneller, aber
  // spürbar träger.
  const gefiltert = useMemo(() => {
    if (!baum) return null;
    if (!suche.trim()) return baum;

    const s = suche.toLowerCase();

    const trifft = (k: SanitaerBaumknoten) =>
      k.name.toLowerCase().includes(s)
      || (k.zusatz ?? '').toLowerCase().includes(s);

    return baum
      .map((g) => ({
        ...g,
        kinder: (g.kinder ?? [])
          .map((r) => ({
            ...r,
            kinder: (r.kinder ?? []).filter((a) => trifft(a) || trifft(r)),
          }))
          .filter((r) => trifft(r) || (r.kinder ?? []).length > 0),
      }))
      .filter((g) => trifft(g) || (g.kinder ?? []).length > 0);
  }, [baum, suche]);

  const umschalten = (schluessel: string) => {
    const neu = new Set(offen);
    if (neu.has(schluessel)) neu.delete(schluessel); else neu.add(schluessel);
    setOffen(neu);
  };

  const seitenkopf = (
    <PageHeader
      titel="Verortung"
      beschreibung={'Geschoss → Raum → Gegenstandsart → Gegenstand: was steht in welchem Raum - Rohre und '
        + 'Gegenstände zusammen.'}
    />
  );

  if (fehler) {
    return (
      <>
        {seitenkopf}
        <ErrorState
          meldung="Die Verortung der Sanitärkomponenten konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((v) => v + 1)}
        />
      </>
    );
  }

  return (
    <>
      {seitenkopf}

      {/* Befund B1: 59 von 2.202 Gegenständen mit Anschlusswert lassen sich keinem Abwassernetzteil zuordnen
          (docs/PLAN-2026-09-20-sanitaermodul.md). Ein eigener Ast "ohne Netzteil" ist Auftrag, aber KEINE Route und
          KEIN Endpunkt liefert die Zuordnung Gegenstand → Netzteil - der Vorbehalt macht die Lücke sichtbar, statt sie
          mit geratenen Zahlen zu füllen (siehe Kopfkommentar). */}
      <Vorbehalte eintraege={[VORBEHALT_B1]} />

      {kopf && (
        <KpiReihe>
          <KpiCard titel="Geschosse" wert={kopf.geschosse} />
          <KpiCard titel="Räume" wert={kopf.raeume} />
          <KpiCard titel="Elemente" wert={kopf.elemente} />
        </KpiReihe>
      )}

      <ContentSection
        titel="Verortung der Komponenten"
        beschreibung={'Rohre und Gegenstände stehen zusammen — sie liegen in zwei '
          + 'Tabellen, aber im selben Raum. Ein Raum lässt sich aufklappen; seine '
          + 'Bauteile werden dann geholt.'}
        werkzeuge={(
          <div className="san-werkzeuge">
            <Feld
              beschriftung="Suche" art="suche" wert={suche} breite="drittel"
              beiAenderung={(w) => setSuche((w as string) ?? '')}
              platzhalter="Raum, Geschoss oder Art …"
            />
            <Feld art="haken" beschriftung="nur Geräte und Abläufe" wert={nurGeraete}
              beiAenderung={(w) => setNurGeraete(Boolean(w))} />
          </div>
        )}
      >
        {!gefiltert ? (
          <LoadingState text="Verortung abrufen …" zeilen={10} spalten={3} />
        ) : gefiltert.length === 0 ? (
          <EmptyState
            titel={suche
              ? 'Kein Geschoss und kein Raum passt zur Suche.'
              : 'Für dieses Modell ist noch keine Sanitärkomponente verortet.'}
            erklaerung={suche
              ? 'Die Suche greift auf Geschoss, Raum und Art.'
              : 'Die Verortung entsteht aus der Sanitäraufnahme; sie ordnet jedes Bauteil einem Raum zu.'}
            aktion={suche ? (
              <button type="button" className="m-knopf" onClick={() => setSuche('')}>Suche aufheben</button>
            ) : undefined}
          />
        ) : (
          <div className="san-baum" data-sanitaerbaum>
            {gefiltert.map((g) => (
              <Geschoss key={g.name} knoten={g} offen={offen}
                        umschalten={umschalten} api={api}
                        projektId={projektId} />
            ))}
          </div>
        )}
      </ContentSection>
    </>
  );
}

function Geschoss({ knoten, offen, umschalten, api, projektId }: {
  knoten: SanitaerBaumknoten;
  offen: Set<string>;
  umschalten: (s: string) => void;
  api: ApiClient;
  projektId: string;
}) {
  const schluessel = `g:${knoten.name}`;
  const auf = offen.has(schluessel);

  return (
    <div className="san-baum-ast">
      <button type="button" className="san-baum-knoten san-baum-geschoss" aria-expanded={auf}
        onClick={() => umschalten(schluessel)}>
        <span className="san-baum-zeichen" aria-hidden="true">{auf ? '▾' : '▸'}</span>
        <span className="san-baum-name">{knoten.name}</span>
        <span className="schwach">
          {formatiereZahl(knoten.raeume, 0)} Räume · {formatiereZahl(knoten.anzahl, 0)} Elemente
          {knoten.laengeM > 0 && ` · ${formatiereZahl(knoten.laengeM)} m`}
        </span>
      </button>

      {auf && (knoten.kinder ?? []).map((r) => (
        <Raum key={`${knoten.name}/${r.name}`} geschoss={knoten.name}
              knoten={r} offen={offen} umschalten={umschalten}
              api={api} projektId={projektId} />
      ))}
    </div>
  );
}

function Raum({ geschoss, knoten, offen, umschalten, api, projektId }: {
  geschoss: string;
  knoten: SanitaerBaumknoten;
  offen: Set<string>;
  umschalten: (s: string) => void;
  api: ApiClient;
  projektId: string;
}) {
  const schluessel = `r:${geschoss}/${knoten.name}`;
  const auf = offen.has(schluessel);

  const [inhalt, setInhalt] = useState<SanitaerRaum | null>(null);
  const [raumFehler, setRaumFehler] = useState<string | null>(null);
  const [raumVersuch, setRaumVersuch] = useState(0);

  // Ein gescheiterter Abruf steht als Fehler mit „Erneut versuchen“ da - früher wurde er stillschweigend zu einem
  // leeren Raum, und ein leerer Raum ist eine Aussage über den Bestand (TS9b1b).
  useEffect(() => {
    if (!auf || inhalt) return;
    setRaumFehler(null);

    api.sanitaerRaum(projektId,
                     knoten.name === 'ohne Raum' ? undefined : knoten.name,
                     geschoss === 'ohne Geschoss' ? undefined : geschoss)
      .then(setInhalt)
      .catch((e) => setRaumFehler(e instanceof Error ? e.message : String(e)));
  }, [auf, inhalt, api, projektId, knoten.name, geschoss, raumVersuch]);

  const artspalten: Spalte<any>[] = [
    {
      schluessel: 'gattung', titel: '', breite: '40px', sortierbar: false,
      wert: (a) => a.gattung,
      inhalt: (a) => <>{a.gattung === 'gegenstand' ? '◆' : '─'}</>,
    },
    {
      schluessel: 'art', titel: 'Art', immerSichtbar: true,
      wert: (a) => (ARTNAMEN[a.name] ?? a.name),
    },
    {
      schluessel: 'kreis', titel: 'Kreis',
      wert: (a) => (a.zusatz ? KREISNAMEN[a.zusatz] ?? a.zusatz : ''),
      inhalt: (a) => (
        <span className="schwach">
          {a.zusatz ? KREISNAMEN[a.zusatz] ?? a.zusatz : ''}
        </span>
      ),
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (a) => a.anzahl },
    {
      schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm',
      wert: (a) => (a.laengeM > 0 ? a.laengeM : null),
    },
    {
      schluessel: 'du', titel: 'Anschlusswert', einheit: 'DU', ausrichtung: 'rechts',
      wert: (a) => (a.summeDu > 0 ? a.summeDu : null),
    },
    {
      schluessel: 'produkt', titel: 'mit Produkt', art: 'zahl',
      wert: (a) => (a.mitProdukt > 0 ? a.mitProdukt : null),
    },
  ];

  const gegenstandsspalten: Spalte<any>[] = [
    {
      schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (x) => x.typ ?? x.familie,
      inhalt: (x) => <span className="schwach">{x.typ ?? x.familie ?? ''}</span>,
    },
    {
      schluessel: 'du', titel: 'Anschlusswert', einheit: 'DU', ausrichtung: 'rechts',
      wert: (x) => x.anschlusswertDU,
      inhalt: (x) => <WertEinheit wert={x.anschlusswertDU ?? null} rechts />,
    },
    {
      schluessel: 'modell', titel: 'Herkunftsmodell', wert: (x) => x.herkunftsmodell,
      inhalt: (x) => <span className="schwach">{x.herkunftsmodell ?? ''}</span>,
    },
  ];

  // Vierte Baumebene: Gegenstand gruppiert nach Gegenstandsart, mit der
  // DU-Summe je Art - die dritte Gliederungsebene des Auftrags
  // (Geschoss → Raum → Gegenstandsart → Gegenstand).
  const gegenstandsarten = useMemo(() => {
    if (!inhalt) return [];
    const karte = new Map<string, {
      art: string; anzahl: number; summeDu: number;
      gegenstaende: SanitaerRaum['gegenstaende'];
    }>();
    for (const g of inhalt.gegenstaende) {
      const schl = g.art ?? 'sonstiges';
      let e = karte.get(schl);
      if (!e) {
        e = { art: schl, anzahl: 0, summeDu: 0, gegenstaende: [] };
        karte.set(schl, e);
      }
      e.anzahl += 1;
      e.summeDu += g.anschlusswertDU ?? 0;
      e.gegenstaende.push(g);
    }
    return [...karte.values()].sort((a, b) => b.anzahl - a.anzahl);
  }, [inhalt]);

  return (
    <div className="san-baum-einzug">
      <button type="button" className="san-baum-knoten" aria-expanded={auf}
        onClick={() => umschalten(schluessel)}>
        <span className="san-baum-zeichen" aria-hidden="true">{auf ? '▾' : '▸'}</span>
        <span className="san-baum-name">{knoten.name}</span>
        <span className="schwach san-baum-zusatz">{knoten.zusatz ?? ''}</span>
        <span className="schwach">
          {formatiereZahl(knoten.anzahl, 0)}
          {knoten.gegenstaende > 0 && ` · ${formatiereZahl(knoten.gegenstaende, 0)} Gegenstände`}
          {knoten.summeDu > 0 && ` · ΣDU ${formatiereZahl(knoten.summeDu)}`}
        </span>
      </button>

      {auf && (
        <div className="san-baum-einzug">
          <DataTable
            name={`Bauteilarten in ${knoten.name}`}
            spalten={artspalten}
            zeilen={knoten.kinder ?? []}
            schluessel={(a) => `${a.gattung}/${a.name}/${a.zusatz ?? ''}`}
            suche={false}
            spaltenwahl={false}
            dicht
            leer={(
              <EmptyState
                klein
                titel="In diesem Raum steht kein Sanitärbauteil."
                erklaerung="Der Raum ist im Raumbuch geführt, trägt aber kein Bauteil des Gewerks."
              />
            )}
          />

          {raumFehler && (
            <ErrorState klein meldung="Die Bauteile dieses Raums konnten nicht geholt werden." technisch={raumFehler}
              beiErneutVersuchen={() => setRaumVersuch((v) => v + 1)} />
          )}
          {!inhalt && !raumFehler && (
            <LoadingState text="Gegenstände werden geholt …" art="spinner" />
          )}

          {inhalt && gegenstandsarten.length > 0 && (
            <ContentSection
              titel={`${inhalt.gegenstaende.length} Gegenstände in ${gegenstandsarten.length} Arten`}
              aufklappbar
              standardOffen={false}
              nackt
            >
              {gegenstandsarten.map((ga) => (
                <Gegenstandsart
                  key={ga.art}
                  raumSchluessel={schluessel}
                  ga={ga}
                  spalten={gegenstandsspalten}
                  offen={offen}
                  umschalten={umschalten}
                />
              ))}
            </ContentSection>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Die vierte Ebene: eine Gegenstandsart mit ihrer DU-Summe, aufklappbar bis
 * zum einzelnen Gegenstand. Eigener Knoten im selben `offen`-Set wie
 * Geschoss und Raum - ein Präfix (`ga:`) hält die Schlüssel auseinander.
 */
function Gegenstandsart({
  raumSchluessel, ga, spalten, offen, umschalten,
}: {
  raumSchluessel: string;
  ga: { art: string; anzahl: number; summeDu: number; gegenstaende: SanitaerRaum['gegenstaende'] };
  spalten: Spalte<any>[];
  offen: Set<string>;
  umschalten: (s: string) => void;
}) {
  const schluessel = `ga:${raumSchluessel}/${ga.art}`;
  const auf = offen.has(schluessel);

  return (
    <div className="san-baum-ast">
      <button type="button" className="san-baum-knoten" aria-expanded={auf}
        onClick={() => umschalten(schluessel)}>
        <span className="san-baum-zeichen" aria-hidden="true">{auf ? '▾' : '▸'}</span>
        <span className="san-baum-name">{ARTNAMEN[ga.art] ?? ga.art}</span>
        <span className="schwach">
          {formatiereZahl(ga.anzahl, 0)} Gegenstände
          {ga.summeDu > 0 && ` · ΣDU ${formatiereZahl(ga.summeDu)}`}
        </span>
      </button>

      {auf && (
        <div className="san-baum-einzug">
          {/* Vorher `slice(0, 100)` ohne Hinweis - `DataTable` virtualisiert
              ab 200 Zeilen und zeigt trotzdem alle. */}
          <DataTable
            name={`Gegenstände: ${ARTNAMEN[ga.art] ?? ga.art}`}
            spalten={spalten}
            zeilen={ga.gegenstaende}
            schluessel={(x) => String(x.id)}
            dicht
            ausfuhr
            ausfuhrDateiname={`gegenstaende-${ga.art}`}
          />
        </div>
      )}
    </div>
  );
}
